import React from 'react';
import { Document } from '@react-pdf/renderer';
import type { AdmissionApplication, AdmissionCycle } from '../../../types/admissions';
import {
  Page, DataTable, SummaryLine, tally, applicationStatusTone, fmtClassName,
  type SchoolBranding, type DocumentMeta,
} from '../../../utils/pdf';
import { applicationExportColumns, classNameOf } from '../../../utils/admissionRecords';
import { statusLabel } from '../../common/StatusChip';
import { exportPdfColumns } from './exportColumns';

interface Props {
  apps: AdmissionApplication[];
  cycle: AdmissionCycle;
  branding: SchoolBranding;
  meta: DocumentMeta;
}

/** ApplicationsListDocument — the applications register for a cycle (or the
 *  filtered part of it), landscape. Columns come from the same definition
 *  as the Excel export; the PDF shows the ones that fit on the page. */
const ApplicationsListDocument: React.FC<Props> = ({ apps, cycle, branding, meta }) => {
  const columns = exportPdfColumns(applicationExportColumns(cycle), a => applicationStatusTone(a.status));

  // How the list breaks down — the summary under the header.
  const byStatus = tally(apps, a => statusLabel({ kind: 'application', value: a.status }), a => applicationStatusTone(a.status));
  const byClass = tally(apps, a => fmtClassName(classNameOf(a)));

  return (
    <Document title={meta.title} author={branding.name}>
      <Page
        branding={branding}
        meta={meta}
        orientation="landscape"
        official={{ details: [
          ...(meta.subtitle ? [{ value: meta.subtitle }] : []),
          { value: `${apps.length} application${apps.length === 1 ? '' : 's'}` },
        ] }}
      >
        <SummaryLine
          groups={[
            { label: 'By status', items: byStatus },
            // One class says nothing the header doesn't.
            ...(byClass.length > 1 ? [{ label: 'By class', items: byClass }] : []),
          ]}
        />
        <DataTable columns={columns} rows={apps} zebra compact />
      </Page>
    </Document>
  );
};

export default ApplicationsListDocument;
